import React, { useContext, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { canViewBranchReport, isBranchPinned, toRoleId } from "../utils/roles";
import { ALL_BRANCHES } from "../utils/branchDisplay";
import { isPawnStore, isTravelStore } from "../utils/moduleNav";
// The report chrome the pawn reports established — page/table flex rules, the
// one-checkbox date window, the sort button. They live under PawnShop/ because
// that is where they were first needed, but nothing in them is pawn-specific
// (no pawn-mode, no CSS-var accent), and a branch report that laid its filters
// out differently from every other report in the app would be the odd one out
// for no reason. Worth moving somewhere shared the next time a screen outside
// pawn needs them.
import {
  reportFixedSx,
  reportPageSx,
  reportResultsCardSx,
  reportWideTableSx,
} from "./PawnShop/reportLayout";
import ReportDateWindow from "./PawnShop/reportDateWindow";
import ReportSortControl, {
  DEFAULT_SORT,
  sortRows,
} from "./PawnShop/reportSort";

/* ============================================================================
 * Branch Report — which of my shops did how much
 * ----------------------------------------------------------------------------
 * The point of the whole branch feature for an owner. Every other screen treats
 * a branch as a label on a row; this is the one place it is a dimension you can
 * add up by.
 *
 * TWO SHAPES, ONE ENDPOINT. GET /api/auth/branch-report/:store_id decides from
 * the caller's role and what they asked for:
 *
 *   viewing ONE branch    that branch's figures on their own
 *   viewing ALL branches  every branch side by side, plus the store total
 *
 * A single-branch store never gets the second shape — there is nothing to
 * compare — and the server collapses an "all" request down to that one branch
 * rather than erroring, so nothing here has to special-case it.
 *
 * THE TOTAL IS SUMMED FROM THE BRANCH ROWS on the server, not re-queried
 * without a GROUP BY, so it cannot disagree with the rows it is printed under.
 * That is the first thing anybody checks.
 *
 * AGGREGATION IS THE ONE PLACE A BRANCH FILTER BELONGS. Lists stay store-wide
 * everywhere else: a booking taken at one branch has to be payable at another.
 * See utils/branchDisplay.js.
 *
 * TWO HOSTS, ONE SCREEN. It hangs off the Daily Tick rail at /branch-report,
 * and — for a pawn store, where the rail entry is not drawn at all — off the
 * pawn Reports strip at /pawn/reports?type=branch, rendered `embedded` so that
 * screen supplies the heading. Same component either way.
 *
 * OWNER'S EYES ONLY. canViewBranchReport() decides, and it is checked HERE
 * rather than only where the entry points are drawn: a URL typed into the bar
 * is a way in that no sidebar filter covers, and the server answers this
 * endpoint for anybody (it narrows an over-wide request to the caller's own
 * branch instead of refusing it).
 * ========================================================================== */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const BLUE = "#2563EB";
const BLUE_PALE = "#EFF6FF";
const GREEN = "#059669";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";

// Which metrics carry money, and therefore earn a second column.
//
// Hardcoded rather than inferred from the data: the ticket metrics select NULL
// as their amount by design (a ticket has no value), so a rule like "show the
// column when some row is non-zero" would give them a column of zeros in a busy
// month and take it away in a quiet one, shifting every column beside it.
const MONEY_METRICS = new Set(["loans", "receipts", "bookings"]);

// Which metrics belong to a module, so a pawn shop is not shown a column of
// zeroed travel bookings and a travel office is not shown pawn loans. A metric
// absent from here (the ticket ones) is shown to everybody — every store raises
// tickets.
const METRIC_MODULE = {
  loans: "pawn",
  receipts: "pawn",
  bookings: "travel",
};

// Local calendar date as YYYY-MM-DD. en-CA is ISO order and, unlike
// toISOString(), does not shift the day backwards for anyone east of UTC.
const todayISO = () => new Date().toLocaleDateString("en-CA");

const monthStartISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
};

const countFmt = (n) => Number(n || 0).toLocaleString("en-IN");

const moneyFmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });

const BranchReport = ({ embedded = false }) => {
  const {
    currentStoreInfo,
    userInfo,
    role,
    currentBranch,
    branchScope,
    canViewAllBranches,
  } = useContext(AutoTaskAuthContext) || {};

  const storeId = currentStoreInfo?.id ?? userInfo?.store_id ?? null;
  const roleId = toRoleId(role ?? userInfo?.role);
  const pinned = isBranchPinned(roleId);

  // Who is allowed to be here at all. Read once and used in two places: it
  // holds the fetch back below, so a person who may not see the figures never
  // asks the server for them, and it swaps the whole screen for a notice at the
  // bottom of this component.
  const allowed = canViewBranchReport(roleId);

  // How many shops there are to compare, off the session's own store row
  // (buildSessionPayload flattens branch_count on beside the list it counted).
  const storeBranchCount = Number(
    currentStoreInfo?.branch_count ?? currentStoreInfo?.branches?.length ?? 0,
  );

  // The period. A branch report answers "which shop did how much", which is a
  // question about a stretch of time, so it opens on the month to date rather
  // than on the single day the transaction reports default to.
  const [dateMode, setDateMode] = useState("range");
  const [singleDate, setSingleDate] = useState(todayISO);
  const [fromDate, setFromDate] = useState(monthStartISO);
  const [toDate, setToDate] = useState(todayISO);

  // What to report on.
  //
  // Opens on EVERY branch for anyone allowed to see that, because comparing the
  // shops is what this screen is for — landing on the one branch the session
  // happens to be in makes it look like the report only knows about one place,
  // and the first thing anybody does is change the dropdown. Everyone else
  // opens on the branch they are in, and a pinned user cannot change it.
  //
  // This is REPORT scope and nothing else. It is deliberately local state
  // rather than the session's all-branches view: choosing "All branches" here
  // widens the report without taking creation away anywhere in the app, which
  // only the branch switcher does.
  //
  // The store's own branch count is honoured beside canViewAllBranches for the
  // same reason the pawn chip reads it: that flag is one field carried through
  // three login paths, and a session that reached here with a stale false would
  // open on the single branch it happens to be in — which is precisely the
  // "the report only knows about one place" first impression above. `offerAll`
  // below already falls back the same way, so the dropdown and the value it
  // opens on agree.
  const [selection, setSelection] = useState(() =>
    canViewAllBranches || storeBranchCount > 1
      ? ALL_BRANCHES
      : (branchScope?.branchId ?? ALL_BRANCHES),
  );

  const [sort, setSort] = useState(DEFAULT_SORT);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const from = dateMode === "single" ? singleDate : fromDate;
  const to = dateMode === "single" ? singleDate : toDate;

  const userBranchId = userInfo?.branch_id ?? currentBranch?.id ?? null;

  const fetchReport = React.useCallback(async () => {
    if (!allowed) return;
    if (storeId === null || storeId === undefined) return;

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/auth/branch-report/${storeId}`,
        {
          params: {
            branch_id: selection ?? undefined,
            // Who is asking. Sent by the caller today, exactly as store_id
            // already is everywhere in this app — the server re-resolves the
            // scope from it, and this endpoint only ever reads.
            role: roleId ?? undefined,
            user_branch_id: userBranchId ?? undefined,
            from: from || undefined,
            to: to || undefined,
          },
        },
      );

      if (!res?.data?.success) {
        throw new Error(res?.data?.statusMessage || "Failed to load the report");
      }
      setReport(res.data.data);
    } catch (err) {
      setReport(null);
      setError(
        err?.response?.data?.statusMessage ||
          err?.message ||
          "Failed to load the report",
      );
    } finally {
      setLoading(false);
    }
  }, [allowed, storeId, selection, roleId, userBranchId, from, to]);

  // The report loads the moment a date or the branch changes — no View press.
  // The button beside the filters is for retrying after a failure and for an
  // explicit refresh; it is not how the screen is normally driven.
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const scope = report?.scope;
  const totals = report?.totals || {};

  // Memoised because it feeds the sort below: a fresh [] on every render would
  // re-sort every row on every keystroke elsewhere on the page.
  const branches = useMemo(() => report?.branches || [], [report?.branches]);

  // Which columns this store's modules justify. Filtered from the metric list
  // the server sends rather than from a local copy, so a metric added on the
  // backend appears here without a frontend change — it just cannot be
  // module-gated until somebody adds it to METRIC_MODULE.
  const metrics = useMemo(() => {
    const all = report?.metrics || [];
    const runsPawn = isPawnStore(currentStoreInfo);
    const runsTravel = isTravelStore(currentStoreInfo);

    return all.filter((m) => {
      const module = METRIC_MODULE[m.key];
      if (module === "pawn") return runsPawn;
      if (module === "travel") return runsTravel;
      return true;
    });
  }, [report?.metrics, currentStoreInfo]);

  // Sorting is client-side and applied last, so it reorders what is on screen
  // without changing which branches are shown or what the total adds up to.
  const sortOptions = useMemo(
    () => [
      { key: "branch", label: "Branch name" },
      ...metrics.map((m) => ({ key: m.key, label: m.label })),
      ...metrics
        .filter((m) => MONEY_METRICS.has(m.key))
        .map((m) => ({ key: m.key + "_amount", label: m.label + " (value)" })),
    ],
    [metrics],
  );

  const sortAccessors = useMemo(() => {
    const map = { branch: (r) => r.branch_name };
    for (const m of metrics) {
      map[m.key] = (r) => Number(r[m.key] || 0);
      map[m.key + "_amount"] = (r) => Number(r[m.key + "_amount"] || 0);
    }
    return map;
  }, [metrics]);

  const rows = useMemo(
    () => sortRows(branches, sort, sortAccessors),
    [branches, sort, sortAccessors],
  );

  // The store's own line is only worth printing when there is more than one row
  // above it to add up. On a single branch it would just repeat that row.
  const showTotals = rows.length > 1;

  // Before the first report comes back there is nothing to fill the picker
  // with, so the store's cached branch list stands in. The two shapes differ
  // (branch_id/branch_name from the report, id/name from the store row), which
  // is why both are read below.
  const branchOptions = branches.length
    ? branches
    : currentStoreInfo?.branches ?? [];

  // Whether to offer "All branches" at all. The server's answer wins once a
  // report has come back; until then the session's own flag stands in, so the
  // option does not appear and then vanish on first load.
  const offerAll = scope
    ? scope.can_view_all_branches
    : branchScope?.mode === ALL_BRANCHES || branchOptions.length > 1;

  // What the picker shows. Held blank until the value it is set to is actually
  // one of the options, which it is not for the first render of a cold start:
  // the branch list has not arrived and MUI would log the selection as
  // out of range.
  const selectionInRange =
    (selection === ALL_BRANCHES && offerAll) ||
    branchOptions.some((b) => (b.branch_id ?? b.id) === selection);

  const headCellSx = {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: INK_MUTED,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    bgcolor: SURFACE,
    borderBottom: "1px solid " + BORDER,
    whiteSpace: "nowrap",
    py: 1,
  };

  const bodyCellSx = {
    fontSize: "0.8rem",
    color: INK_SOFT,
    borderBottom: "1px solid " + BORDER,
    whiteSpace: "nowrap",
    py: 0.75,
  };

  // Not for this person. Rendered instead of the screen rather than as a
  // redirect: whoever followed the link deserves to be told why there is
  // nothing here, and bouncing them somewhere else reads as a broken link.
  //
  // Checked BEFORE the pawn hand-off below, so somebody who may not read the
  // report is told so rather than quietly deposited on a different report.
  if (!allowed) {
    return (
      <Box sx={reportPageSx(embedded)}>
        <Alert severity="info" sx={{ ...reportFixedSx, m: embedded ? 0 : 1 }}>
          The branch report is available to the store owner only.
        </Alert>
      </Box>
    );
  }

  // A PAWN STORE READS THIS INSIDE ITS OWN MODULE. Only an old bookmark or a
  // typed URL still arrives at the Daily Tick route, and serving it here would
  // drop a pawn owner into the other shell — no pawn sidebar, no way back to
  // the reports they were reading. Sent on to the same screen in its proper
  // place instead, `replace` so Back does not bounce off this route again.
  //
  // Only when standalone: embedded IS the pawn Reports screen, and redirecting
  // there from inside it is a loop.
  if (!embedded && isPawnStore(currentStoreInfo)) {
    return <Navigate to="/pawn/reports?type=branch" replace />;
  }

  return (
    <Box sx={reportPageSx(embedded)}>
      {/* ── Heading (suppressed when embedded — the pawn Reports screen draws
          its own heading and report switcher above this) ───────────────── */}
      {!embedded && (
        <Box sx={{ ...reportFixedSx, mb: 1.5 }}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: INK }}>
            Branch Report
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
              {report?.store_name || currentStoreInfo?.name || "This store"}
            </Typography>
            {scope && (
              <Chip
                size="small"
                icon={
                  scope.mode === ALL_BRANCHES ? (
                    <PublicRoundedIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <PlaceRoundedIcon sx={{ fontSize: 14 }} />
                  )
                }
                label={
                  scope.mode === ALL_BRANCHES
                    ? "All " + scope.branch_count + " branches"
                    : scope.branch_name || "One branch"
                }
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  bgcolor: BLUE_PALE,
                  color: "#1E40AF",
                  border: "1px solid #BFDBFE",
                  "& .MuiChip-icon": { color: "#1E40AF", ml: 0.5 },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          ...reportFixedSx,
          mb: 1.5,
          p: { xs: 1.25, sm: 1.5 },
          border: "1px solid " + BORDER,
          borderRadius: "12px",
          bgcolor: SURFACE,
        }}
      >
        <Grid container spacing={1.5} alignItems="flex-end">
          <ReportDateWindow
            label="Period"
            dateMode={dateMode}
            setDateMode={setDateMode}
            singleDate={singleDate}
            setSingleDate={setSingleDate}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
          />

          <Grid item xs={12} sm="auto">
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="branch-report-scope">Branch</InputLabel>
              <Select
                labelId="branch-report-scope"
                label="Branch"
                value={selectionInRange ? selection : ""}
                onChange={(e) => setSelection(e.target.value)}
                // A pinned user is confined to their own branch, and the server
                // quietly downgrades any other request back to it. Locking the
                // control says so up front rather than letting them pick one
                // thing and then serving them another.
                disabled={pinned}
              >
                {offerAll && (
                  <MenuItem value={ALL_BRANCHES}>All branches</MenuItem>
                )}
                {branchOptions.map((b) => (
                  <MenuItem
                    key={b.branch_id ?? b.id}
                    value={b.branch_id ?? b.id}
                  >
                    {b.branch_name ?? b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs="auto">
            <Button
              onClick={fetchReport}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <SearchRoundedIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.8rem",
                bgcolor: BLUE,
                color: "#fff",
                px: 2,
                "&:hover": { bgcolor: "#1D4ED8" },
              }}
            >
              View
            </Button>
          </Grid>

          <Grid item xs="auto">
            <ReportSortControl
              options={sortOptions}
              value={sort}
              onChange={setSort}
              disabled={rows.length === 0}
            />
          </Grid>
        </Grid>
      </Card>

      {error && (
        <Alert severity="error" sx={{ ...reportFixedSx, mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {/* ── Results ─────────────────────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          ...reportResultsCardSx,
          border: "1px solid " + BORDER,
          borderRadius: "12px",
          bgcolor: SURFACE,
        }}
      >
        <TableContainer sx={reportWideTableSx}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, minWidth: 150 }}>
                  Branch
                </TableCell>
                {metrics.map((m) => (
                  <React.Fragment key={m.key}>
                    <TableCell align="right" sx={headCellSx}>
                      {m.label}
                    </TableCell>
                    {MONEY_METRICS.has(m.key) && (
                      <TableCell
                        align="right"
                        sx={{
                          ...headCellSx,
                          borderRight: "1px solid " + BORDER,
                        }}
                      >
                        Value
                      </TableCell>
                    )}
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={1 + metrics.length * 2}
                    sx={{ ...bodyCellSx, textAlign: "center", py: 4 }}
                  >
                    <Typography sx={{ fontSize: "0.85rem", color: INK_MUTED }}>
                      {error
                        ? "Nothing to show — the report could not be loaded."
                        : "No branches to report on for this period."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {rows.map((row) => (
                <TableRow key={row.branch_id} hover>
                  <TableCell sx={bodyCellSx}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: INK,
                        }}
                      >
                        {row.branch_name}
                      </Typography>
                      {row.is_main && (
                        <Chip
                          label="Main"
                          size="small"
                          sx={{
                            height: 17,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            bgcolor: "#F1F5F9",
                            color: "#334155",
                            border: "1px solid " + BORDER,
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>

                  {metrics.map((m) => (
                    <React.Fragment key={m.key}>
                      <TableCell align="right" sx={bodyCellSx}>
                        {countFmt(row[m.key])}
                      </TableCell>
                      {MONEY_METRICS.has(m.key) && (
                        <TableCell
                          align="right"
                          sx={{
                            ...bodyCellSx,
                            borderRight: "1px solid " + BORDER,
                            fontWeight: 600,
                            color: INK,
                          }}
                        >
                          {moneyFmt(row[m.key + "_amount"])}
                        </TableCell>
                      )}
                    </React.Fragment>
                  ))}
                </TableRow>
              ))}
            </TableBody>

            {/* The store's line, stuck to the bottom edge so it stays readable
                while the branches above it scroll. */}
            {showTotals && (
              <TableBody>
                <TableRow
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    bgcolor: "#F8FAFC",
                    "& td": {
                      borderTop: "2px solid " + BORDER,
                      borderBottom: 0,
                    },
                  }}
                >
                  <TableCell
                    sx={{ ...bodyCellSx, fontWeight: 700, color: GREEN }}
                  >
                    {report?.store_name || "Store total"}
                  </TableCell>
                  {metrics.map((m) => (
                    <React.Fragment key={m.key}>
                      <TableCell
                        align="right"
                        sx={{ ...bodyCellSx, fontWeight: 700, color: INK }}
                      >
                        {countFmt(totals[m.key])}
                      </TableCell>
                      {MONEY_METRICS.has(m.key) && (
                        <TableCell
                          align="right"
                          sx={{
                            ...bodyCellSx,
                            borderRight: "1px solid " + BORDER,
                            fontWeight: 700,
                            color: GREEN,
                          }}
                        >
                          {moneyFmt(totals[m.key + "_amount"])}
                        </TableCell>
                      )}
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default BranchReport;
